import { api, APIError } from "./api";
import { TokenService } from "./tokenService";
import { sendToTerminal } from "./terminalLogger";
import { mapPermissionIdToRole } from "./permissionMapper";
import {
	clearAllAuthStateAndCaches,
	clearImpersonationOnly,
} from "./auth/cleanup";
import { reconcileAuthState } from "./auth/reconcileAuthState";
import { removeKey } from "@/utils/remove-sessionstorage";

// ── Token refresh scheduler ────────────────────────────
// Renova o JWT automaticamente 30 min antes de expirar.
// Independente do Launcher — usa POST /auth/cdr/refresh.
let _refreshTimer: ReturnType<typeof setTimeout> | null = null;
const REFRESH_MARGIN_MS = 30 * 60 * 1000; // 30 min antes de expirar

/** Extrai exp (Unix seconds) do JWT sem biblioteca */
function getJwtExp(token: string): number | null {
	try {
		const payload = JSON.parse(atob(token.split(".")[1]));
		return typeof payload.exp === "number" ? payload.exp : null;
	} catch {
		return null;
	}
}

/**
 * Agenda refresh automático do JWT antes de expirar.
 * Chamado após login, bootstrap e cada refresh bem-sucedido.
 */
export function scheduleTokenRefresh(): void {
	if (_refreshTimer) clearTimeout(_refreshTimer);

	const token = TokenService.getAccessToken();
	if (!token) return;

	const exp = getJwtExp(token);
	if (!exp) return;

	const expiresAt = exp * 1000; // ms
	const now = Date.now();
	const delay = Math.max(expiresAt - now - REFRESH_MARGIN_MS, 10_000); // mín 10s

	_refreshTimer = setTimeout(async () => {
		try {
			const ok = await refreshToken();
			if (ok) {
				sendToTerminal("[AUTH] Token renovado automaticamente via scheduler");
			} else {
				sendToTerminal(
					"[AUTH] Falha ao renovar token via scheduler — sessão pode expirar",
				);
			}
		} catch {
			// Silencioso — o retry em 401 do api.ts é o fallback
		}
	}, delay);
}

// Interface para a permissão do usuário
interface Permission {
	perm_id: number;
	perm_descricao: string;
	perm_path: string;
}

interface LoginResponse {
	success: boolean;
	message: string;
	data: {
		accessToken: string;
		refreshToken?: string;
		CopyToken?: string;
		usuario: {
			id: number;
			nome: string;
			login?: string;
			tipo?: string;
		};
		permissions?: Permission[];
		permissionsConfig?: any[];
		isAdmin?: boolean;
	};
}

// Novo payload: /auth/cdr
interface NewLoginUserPermission {
	path: string;
	perm_id: number;
	name?: string;
	component_path?: string;
	meta?: any;
	has_params?: boolean;
	params?: any[];
	acoes?: {
		criar?: boolean;
		visualizar?: boolean;
		editar?: boolean;
		excluir?: boolean;
	};
}

interface NewLoginUser {
	user_id: number;
	user_nome: string;
	user_login: string;
	user_isadmin: number | boolean;
	permissoes?: NewLoginUserPermission[];
	permissionsConfig?: any[];
}

interface NewLoginResponse {
	msg: string;
	token: string;
	refreshToken?: string;
	refreshExpiresAt?: string;
	user: NewLoginUser;
}

function normalizeNewLoginResponse(newResp: NewLoginResponse): LoginResponse {
	const perms: Permission[] | undefined = newResp.user.permissoes
		? newResp.user.permissoes.map((p) => {
			const permId = p.perm_id;
			const desc = p.name || p.perm_descricao || '';
			const path = p.path || p.perm_path || '';
			return {
				perm_id: permId,
				perm_descricao: desc,
				perm_path: path,
			};
		})
		: undefined;

	const normalizedResponse = {
		success: true,
		message: newResp.msg || "Login efetuado",
		data: {
			accessToken: newResp.token,
			refreshToken: newResp.refreshToken,
			usuario: {
				id: newResp.user.user_id,
				nome: newResp.user.user_nome,
				login: newResp.user.user_login,
			},
			permissions: perms,
			permissionsConfig: newResp.user.permissionsConfig || [],
			isAdmin: Boolean(newResp.user.user_isadmin),
		},
	};

	// Removido log verbose de normalização - apenas essencial no SET_TOKENS

	return normalizedResponse;
}

/**
 * Efetua o login do usuário no backend (modelo novo /auth/cdr),
 * normaliza a resposta e persiste o accessToken via TokenService.
 * Dispara o evento global 'auth:updated' após atualizar o estado local.
 * @param username Usuário (login)
 * @param password Senha do usuário
 * @returns Objeto normalizado de LoginResponse
 * @throws Error quando a resposta do servidor é inesperada
 */
export async function login(
	username: string,
	password: string,
): Promise<LoginResponse> {
	const loginPayload = { user: username, password };

	// 🛡️ SEGURANÇA: Nunca logar password ou tokens - apenas dados não sensíveis
	sendToTerminal("🔐 LOGIN REQUEST", {
		timestamp: Date.now(),
		user: username,
	});
	// Evitar herdar impersonação de sessão anterior
	try {
		clearImpersonationOnly();
	} catch { }

	try {
		const newResp = await api("/auth/cdr", {
			method: "POST",
			body: JSON.stringify(loginPayload),
		});

		if (newResp && newResp.token && newResp.user) {
			// 🛡️ SEGURANÇA: Log apenas dados não sensíveis - tokens são sanitizados automaticamente
			sendToTerminal("✅ LOGIN SUCCESS", {
				timestamp: Date.now(),
				userId: newResp.user.user_id,
				userName: newResp.user.user_nome,
				isAdmin: Boolean(newResp.user.user_isadmin),
				permissionsCount: newResp.user.permissoes?.length || 0,
			});

			//const userPermission = mapPermissionIdToRole()
			console.log(newResp.user)
			if (newResp.user.permissionsConfig.length === 0) {
				removeKey()
				throw new Error("Usuário sem permissão")
			}

			const normalized = normalizeNewLoginResponse(newResp as NewLoginResponse);

			// Armazena tokens. setTokens propaga internamente via auth:updated
			// (accessToken, refreshToken e userData) — AuthContext e
			// PermissionContext recebem tudo via evento.
			TokenService.setTokens(
				normalized.data.accessToken,
				normalized.data.refreshToken,
				normalized,
			);

			// Agendar refresh automático antes de expirar
			scheduleTokenRefresh();

			return normalized;
		}
	} catch (permError) {
		/*
		* @description tipa o Erro com o pacote de erro vindo do servidor para ter liberdade de usar o metodos da request com erro.
		* Lançamos um erro com a mesma mensagem de erro vindo do backend
		*/
		if (permError instanceof APIError && permError.status) {
			throw permError
		}
		//Recusar login de forma explícita e segura
		throw new Error(
			permError instanceof Error
				? permError.message
				: "Erro ao validar permissões do login.",
		);
	}

	// Caso a API não retorne o esperado, lança erro explícito
	throw new Error("Falha no login: resposta inesperada do servidor.");
}

/**
 * Tenta renovar a sessão do usuário.
 * 1) Tenta POST /auth/cdr/refresh (renova JWT usando token atual válido);
 * 2) Fallback legacy com refreshToken separado (se existir);
 * 3) Último recurso: checkToken() para validar se o token ainda funciona.
 * @returns true se a sessão foi renovada/validada com sucesso; false caso contrário
 */
export async function refreshToken(): Promise<boolean> {
	try {
		const currentToken = TokenService.getAccessToken();
		const storedRefreshToken = TokenService.getRefreshToken();
		const existingUserData = TokenService.getUserData();

		// 1) Priorizar o refresh token real quando ele existir.
		// Esse fluxo funciona mesmo com o JWT principal já expirado.
		if (storedRefreshToken) {
			const response = await api("/auth/refresh", {
				method: "POST",
				body: JSON.stringify({ refreshToken: storedRefreshToken }),
			});
			if (response?.token) {
				TokenService.setTokens(
					response.token,
					response.refreshToken,
					existingUserData ?? undefined,
				);
				await reconcileAuthState(response.token, {
					reason: "refresh:/auth/refresh",
				});
				scheduleTokenRefresh();
				return true;
			}
			if (response?.success && response.data?.accessToken) {
				TokenService.setTokens(
					response.data.accessToken,
					response.data.refreshToken,
					existingUserData ?? undefined,
				);
				await reconcileAuthState(response.data.accessToken, {
					reason: "refresh:/auth/refresh:legacy-shape",
				});
				scheduleTokenRefresh();
				return true;
			}
			return false;
		}

		// 2) Fallback: usar /auth/cdr/refresh apenas se ainda houver JWT não expirado.
		if (currentToken) {
			try {
				const exp = getJwtExp(currentToken);
				const isExpired = exp ? exp * 1000 <= Date.now() : false;

				if (!isExpired) {
					const response = await api("/auth/cdr/refresh", {
						method: "POST",
					});
					if (response?.token) {
						TokenService.setTokens(
							response.token,
							response.refreshToken,
							existingUserData ?? undefined,
						);
						await reconcileAuthState(response.token, {
							reason: "refresh:/auth/cdr/refresh",
						});
						scheduleTokenRefresh();
						return true;
					}
				}
			} catch {
				// Endpoint pode não existir ou o token já não ser aceito.
			}
		}

		// 3) Último recurso: verificar se token atual ainda é válido
		const ok = await checkToken();
		return ok;
	} catch {
		return false;
	}
}

/** Limiar padrão de pré-renovação proativa do JWT (60s antes de expirar). */
export const TOKEN_PREREFRESH_THRESHOLD_MS = 60_000;

/**
 * Garante um JWT válido ANTES de uma operação crítica (pré-renovação proativa).
 *
 * Centraliza a lógica antes duplicada nos pipelines de ferramentas (orçamento,
 * OneBitmap, etc.). O interceptor de 401 do api.ts já recupera de token expirado
 * com retry; esta função apenas evita o primeiro 401 renovando o token quando
 * falta pouco para expirar.
 *
 * @param thresholdMs Renova proativamente se faltar menos que isso para expirar.
 * @throws Error se não há sessão ou o token expirou e não foi possível renovar.
 */
export async function ensureFreshToken(
	thresholdMs: number = TOKEN_PREREFRESH_THRESHOLD_MS,
): Promise<void> {
	const token = TokenService.getAccessToken();
	if (!token) {
		throw new Error("Usuário não autenticado. Faça login novamente.");
	}

	const exp = getJwtExp(token);

	// Token ilegível → tentar renovar
	if (exp == null) {
		const refreshed = await refreshToken();
		if (!refreshed) {
			throw new Error("Sessão expirada. Faça login novamente.");
		}
		return;
	}

	const remainingMs = exp * 1000 - Date.now();
	if (remainingMs >= thresholdMs) {
		return; // ainda válido com folga
	}

	// Perto de expirar (ou já expirado) → renovar proativamente
	const refreshed = await refreshToken();
	if (!refreshed) {
		// Refresh falhou mas o token ainda não expirou → seguir; o interceptor de
		// 401 do api.ts cobre o caso de expirar durante a operação.
		if (remainingMs > 0) return;
		throw new Error("Sessão expirada. Faça login novamente.");
	}
}

/**
 * Inicializa a autenticação do app.
 * Verifica se já há um accessToken válido ou tenta o fluxo de renovação.
 * @returns true se a autenticação está válida; caso contrário false
 */
export async function initAuth(): Promise<boolean> {
	// Verifica se já tem um token de acesso válido
	if (TokenService.getAccessToken()) {
		await reconcileAuthState(TokenService.getAccessToken() as string, {
			reason: "initAuth:existing-token",
		});
		scheduleTokenRefresh();
		return true;
	}

	// Tenta renovar usando o refresh token
	if (TokenService.hasRefreshToken()) {
		const refreshResult = await refreshToken();
		return refreshResult;
	}

	return false;
}

/**
 * Encerra a sessão do usuário, limpando tokens e redirecionando para '/'.
 */
export async function logout() {
	// Cancelar scheduler de refresh
	if (_refreshTimer) {
		clearTimeout(_refreshTimer);
		_refreshTimer = null;
	}

	// Limpeza centralizada de auth + caches + impersonação
	try {
		clearAllAuthStateAndCaches();
	} catch { }
	window.location.href = "/";
}

/**
 * Obtém as permissões do usuário autenticado (GET /users/me/permissions).
 * @returns Resposta crua da API com a lista de permissões/configurações
 */
export async function getPermissions() {
	try {
		const response = await api("/users/me/permissions");
		return response;
	} catch (error) {
		throw error;
	}
}

// ========= NOVO: Perfil do usuário autenticado =========
export interface MeResponse {
	success: boolean;
	data: {
		id: number;
		nome: string;
		login: string;
		isAdmin?: boolean;
		foto?: string | null;
		ultimoAcesso?: string | null;
		status?: number;
	};
}

/**
 * Retorna os dados do usuário autenticado (GET /auth/me)
 */
export async function getMe(): Promise<MeResponse> {
	try {
		const response = await api("/auth/me", { method: "GET" });
		return response as MeResponse;
	} catch (error) {
		throw error;
	}
}

// ===== Novo: Verificação de token (GET /auth/check) =====
export interface CheckTokenResponse {
	auth?: boolean;
	msg?: string;
}

/**
 * Verifica a validade do token atual junto à API (GET /auth/check).
 * Considera respostas 200 sem corpo como válidas para compatibilidade.
 * @returns true se o token é válido; false caso contrário
 */
export async function checkToken(): Promise<boolean> {
	try {
		const res = await api("/auth/check", { method: "GET" });
		if (typeof res?.auth === "boolean") return !!res.auth;
		// Alguns backends podem retornar 200 sem corpo; considerar válido
		return true;
	} catch (error: any) {
		// 401/403 são tratados como token inválido
		return false;
	}
}

// Obsoleto: fluxo SSO por ticket substituído por SSO via fragmento (#token)
